import { Box, Stack, styled, useMediaQuery, useTheme } from "@mui/system";

import { PropsWithChildren, useMemo } from "react";
import React from "react";

import { Drawer, DrawerProps } from "@mui/material";

import { useUrlFeatureFlag } from "../../app-index";

export type DrawerType = "large" | "medium" | "small";

export type AppDrawerProps = PropsWithChildren<{
  open: boolean;
  onClose: () => void;
  containerRef?: React.RefObject<HTMLDivElement>;
}>;

type DynamicDrawerProps = PropsWithChildren &
  DrawerProps & {
    drawerType: DrawerType;
    hasHeaderPadding?: boolean;
    Header?: React.ReactNode;
    Footer?: React.ReactNode;
  };

const BORDER_RADIUS = 32;
const LARGE_DRAWER_WIDTH = 642;
const MEDIUM_DRAWER_WIDTH = 468;
const SMALL_DRAWER_WIDTH = 380;

export const DynamicDrawer = React.forwardRef<
  HTMLDivElement,
  DynamicDrawerProps
>(
  (
    { children, drawerType, Header, Footer, hasHeaderPadding = true, ...rest },
    ref
  ) => {
    const theme = useTheme();
    const { previewMode } = useUrlFeatureFlag();
    const anchor = rest?.anchor || "right";
    const isLarge = useMediaQuery("(min-width: 701px)");
    const isMedium = useMediaQuery("(min-width: 501px)");
    const isSmall = useMediaQuery("(min-width: 321px)");
    const isSmallest = useMediaQuery("(max-width: 320px)");

    const drawerDepth = useMemo(() => {
      if (drawerType === "small") return 3;
      if (drawerType === "medium") return 1;
      return 0;
    }, [drawerType]);

    const drawerWidth = useMemo(() => {
      if (drawerType === "large") {
        return LARGE_DRAWER_WIDTH;
      }
      if (drawerType === "medium") {
        return MEDIUM_DRAWER_WIDTH;
      }
      if (drawerType === "small") {
        return SMALL_DRAWER_WIDTH;
      }
      return undefined;
    }, [drawerType, isMedium]);

    const drawerPadding = useMemo(() => {
      if (isMedium) return 8;
      if (isSmall) return 6;
      return 3;
    }, [isSmall, isMedium, isLarge]);

    if (!rest.open && previewMode) {
      return null;
    }

    return (
      <Drawer
        anchor={anchor}
        ModalProps={{
          container: ref
            ? (ref as React.RefObject<HTMLDivElement>).current
            : undefined,
          style: {
            width: "100%",
            position: previewMode ? "absolute" : undefined,
          },
        }}
        slotProps={{
          backdrop: {
            style: { position: previewMode ? "absolute" : undefined },
          },
        }}
        PaperProps={{
          style: {
            borderTopLeftRadius: anchor === "right" ? BORDER_RADIUS : 0,
            borderBottomLeftRadius: anchor === "right" ? BORDER_RADIUS : 0,
            borderTopRightRadius: anchor === "left" ? BORDER_RADIUS : 0,
            borderBottomRightRadius: anchor === "left" ? BORDER_RADIUS : 0,
            paddingTop: hasHeaderPadding ? theme.spacing(drawerPadding) : 0,
            marginLeft: theme.spacing(2 * drawerDepth),
            width: drawerWidth,
            maxWidth: `calc(100% - ${16 * drawerDepth}px)`,
            position: previewMode ? "absolute" : undefined,
          },
        }}
        transitionDuration={100}
        {...rest}
      >
        {Header ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              paddingX: hasHeaderPadding ? drawerPadding : undefined,
              justifyContent: "center",
            }}
          >
            {Header}
          </Box>
        ) : null}
        <ContentStack
          paddingX={drawerPadding}
          overflow="auto"
          paddingBottom={isSmallest ? 6 : 10}
          width="100%"
        >
          {children}
        </ContentStack>
        {Footer ? (
          <Box
            sx={{
              position: "absolute",
              backgroundColor: theme.palette.common.white,
              bottom: 0,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              paddingTop: isSmallest ? 1 : 2,
              paddingBottom: isSmallest ? 1 : 4,
              width: "100%",
            }}
          >
            {Footer}
          </Box>
        ) : null}
      </Drawer>
    );
  }
);

const ContentStack = styled(Stack)({
  alignItems: "center",
  display: "flex",
  flexDirection: "column",
  width: "100%",
});
